import { Box, IconButton, Stack } from "@mui/material";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { PRODUCT_STATUS, PRODUCT_TYPE } from "../../../models/Product";
import {
  FilterState,
  useFilterQuery,
  useFilterStore,
  useSetFilterStore,
} from "../../../stores/filterStore";
import { useFormStore } from "../../../stores/formStore";
import { mono } from "../../../theme/colors";
import MultiSelect from "../../atoms/MultiSelect";
import TextField from "../../atoms/TextField";
import SearchIcon from "../../icons/ui-actions/SearchIcon";

const ProductFilters = () => {
  const filterState = useFilterStore();
  const setFilterStore = useSetFilterStore();
  const { setQueryString } = useFilterQuery();
  const formMethods = useForm<FilterState>({
    defaultValues: filterState,
  });

  const resetForm = useFormStore(state => state.reset);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const handleClickSearch = () => {
    const formValues = formMethods.getValues();
    setFilterStore(formValues);
    const getProductsQuery = Object.entries(formMethods.getValues()).reduce(
      (queryParams, [filterKey, filterVal]) => {
        if (!filterVal) {
          return queryParams;
        }
        if (typeof filterVal === "string") {
          return `${queryParams}&${filterKey}=${filterVal}`;
        } else {
          return filterVal && filterVal.length > 0
            ? `${queryParams}&${filterKey}=${filterVal.join(",")}`
            : queryParams;
        }
      },
      "",
    );

    setQueryString(`?${encodeURI(getProductsQuery).slice(1)}`);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleClickSearch();
  };

  return (
    <Stack direction={"row"} spacing={2}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit} data-testid="product-filters-form">
          <Stack spacing={2} direction={"row"}>
            <Box>
              <TextField name="productId" label="Product ID" />
            </Box>
            <Box>
              <TextField name="productName" label="Name" />
            </Box>
            <Box>
              <MultiSelect
                name="productType"
                label="Type"
                options={[
                  { value: PRODUCT_TYPE.VOUCHERS, label: "Vouchers" },
                  {
                    value: PRODUCT_TYPE.DIGITAL_GOODS_AND_SERVICES,
                    label: "Digital goods and services",
                  },
                  {
                    value: PRODUCT_TYPE.PHYSICAL_MERCHANDISE,
                    label: "Physical Merchandise",
                  },
                ]}
              />
            </Box>
            <Box>
              <MultiSelect
                name="status"
                label="Status"
                options={[
                  { value: PRODUCT_STATUS.ACTIVE, label: "Active" },
                  {
                    value: PRODUCT_STATUS.APPROVED,
                    label: "Approved",
                  },
                  {
                    value: PRODUCT_STATUS.COMPLETE,
                    label: "Complete",
                  },
                  {
                    value: PRODUCT_STATUS.DEACTIVATED,
                    label: "Deactivated",
                  },
                  {
                    value: PRODUCT_STATUS.DRAFT,
                    label: "Draft",
                  },
                  {
                    value: PRODUCT_STATUS.PENDING_APPROVAL,
                    label: "Pending Approval",
                  },
                  {
                    value: PRODUCT_STATUS.REJECTED,
                    label: "Rejected",
                  },
                ]}
              />
            </Box>
          </Stack>
          <input type="submit" hidden />
        </form>
      </FormProvider>
      <IconButton
        data-testid="inventory-homepage_product-filter-search_button"
        onClick={handleClickSearch}
        sx={{
          backgroundColor: mono.white,
        }}
      >
        <SearchIcon />
      </IconButton>
    </Stack>
  );
};

export default ProductFilters;
